import { useState, useMemo, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  getTradeHistory,
  type TradeHistoryItem,
  type TradeHistoryFilters,
  type TradeHistoryParams,
  outcomeLabel,
  outcomeColor,
} from "@app/services/adminTradeHistoryApi";
import "./admin-trade-history.css";

const TIME_RANGES = [
  { value: "24h", label: "24H" },
  { value: "3d", label: "3 Days" },
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
  { value: "all", label: "All Time" },
];

function pnlClass(pnl: number | null): string {
  if (pnl === null || pnl === undefined) return "admin-trade-history__pnl-flat";
  if (pnl > 0) return "admin-trade-history__pnl-positive";
  if (pnl < 0) return "admin-trade-history__pnl-negative";
  return "admin-trade-history__pnl-flat";
}

function formatPnl(pnl: number | null): string {
  if (pnl === null || pnl === undefined) return "-";
  const sign = pnl > 0 ? "+" : "";
  return `${sign}${pnl.toFixed(2)}%`;
}

function formatPrice(price: number | null): string {
  if (price === null || price === undefined) return "-";
  if (price < 0.01) return `$${price.toFixed(6)}`;
  if (price < 1) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(2)}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function scoreColor(score: number | null): "high" | "mid" | "low" {
  if (score === null) return "low";
  if (score >= 0.65) return "high";
  if (score >= 0.50) return "mid";
  return "low";
}

function ScoreBar({ score }: { score: number | null }) {
  if (score === null) return <span className="admin-trade-history__score-text">-</span>;
  const cls = scoreColor(score);
  return (
    <div className="admin-trade-history__score-bar">
      <div className="admin-trade-history__score-track">
        <div
          className={`admin-trade-history__score-fill admin-trade-history__score-fill--${cls}`}
          style={{ width: `${score * 100}%` }}
        />
      </div>
      <span className="admin-trade-history__score-text">{score.toFixed(2)}</span>
    </div>
  );
}

/* ── TA Indicator Score Bar ── */
function TaScoreBar({ label, score, maxScore = 100 }: { label: string; score: number | null; maxScore?: number }) {
  if (score === null || score === undefined) return null;
  const pct = Math.min(score / maxScore, 1);
  const cls = pct >= 0.65 ? "high" : pct >= 0.4 ? "mid" : "low";
  return (
    <div className="admin-trade-history__ta-score">
      <span className="admin-trade-history__ta-score-label">{label}</span>
      <div className="admin-trade-history__ta-score-track">
        <div
          className={`admin-trade-history__ta-score-fill admin-trade-history__ta-score-fill--${cls}`}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
      <span className="admin-trade-history__ta-score-value">{score.toFixed(1)}</span>
    </div>
  );
}

/* ── Signal Badge ── */
function SignalBadge({ signal }: { signal: string | null | undefined }) {
  if (!signal) return null;
  const cls = signal.toLowerCase();
  return (
    <span className={`admin-trade-history__signal-badge admin-trade-history__signal-badge--${cls}`}>
      {signal.toUpperCase()}
    </span>
  );
}

/* ── Indicator Table ── */
function IndicatorTable({ indicators }: { indicators: Record<string, unknown> | null | undefined }) {
  if (!indicators) return null;
  const rows: { name: string; value: string; signal?: string }[] = [];
  for (const [key, val] of Object.entries(indicators)) {
    const name = key.replace(/_/g, " ").toUpperCase();
    if (typeof val === "object" && val !== null) {
      for (const [sk, sv] of Object.entries(val as Record<string, unknown>)) {
        rows.push({ name: `${key.toUpperCase()} ${sk.replace(/_/g, " ")}`, value: String(sv) });
      }
    } else if (typeof val === "number") {
      rows.push({ name, value: val.toFixed(2) });
    } else {
      rows.push({ name, value: String(val) });
    }
  }
  return (
    <div className="admin-trade-history__indicator-grid">
      {rows.map((r, i) => (
        <div key={i} className="admin-trade-history__indicator-cell">
          <span className="admin-trade-history__indicator-name">{r.name}</span>
          <span className={`admin-trade-history__indicator-value ${r.signal ? `admin-trade-history__indicator-value--${r.signal}` : ""}`}>
            {r.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Full Analysis Indicator Results ── */
function FullIndicatorResults({ results }: { results: Array<Record<string, unknown>> | null | undefined }) {
  if (!results || results.length === 0) return null;
  return (
    <div className="admin-trade-history__indicator-grid">
      {results.map((r: Record<string, unknown>, i: number) => (
        <div key={i} className="admin-trade-history__indicator-cell">
          <span className="admin-trade-history__indicator-name">{(r.name as string || "").toUpperCase()}</span>
          <span className={`admin-trade-history__indicator-value admin-trade-history__indicator-value--${(r.signal as string || "neutral").toLowerCase()}`}>
            {r.value !== null && r.value !== undefined ? String(r.value) : "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Candlestick Patterns List ── */
function PatternList({ patterns }: { patterns: Array<Record<string, unknown>> | null | undefined }) {
  if (!patterns || patterns.length === 0) return null;
  return (
    <div className="admin-trade-history__pattern-list">
      {patterns.map((p: Record<string, unknown>, i: number) => {
        const sig = (p.signal as string || "").toLowerCase();
        const conf = p.confidence as number;
        return (
          <div key={i} className="admin-trade-history__pattern-item">
            <span className={`admin-trade-history__pattern-signal admin-trade-history__pattern-signal--${sig}`}>
              {sig === "bullish" ? "▲" : "▼"} {(p.pattern_type as string || "").replace(/_/g, " ").toUpperCase()}
            </span>
            {conf !== null && conf !== undefined && (
              <span className="admin-trade-history__pattern-conf">{conf.toFixed(0)}% confidence</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Support / Resistance Levels ── */
function SrLevels({ levels }: { levels: Array<Record<string, unknown>> | null | undefined }) {
  if (!levels || levels.length === 0) return null;
  return (
    <div className="admin-trade-history__sr-list">
      {levels.map((l: Record<string, unknown>, i: number) => {
        const isRes = (l.type as string || "").toLowerCase() === "resistance";
        return (
          <div key={i} className={`admin-trade-history__sr-item ${isRes ? "admin-trade-history__sr-item--res" : "admin-trade-history__sr-item--sup"}`}>
            <span className="admin-trade-history__sr-type">{isRes ? "RES" : "SUP"}</span>
            <span className="admin-trade-history__sr-price">${(l.level as number || 0).toFixed(2)}</span>
            <span className="admin-trade-history__sr-strength">{(l.strength as number || 0).toFixed(2)}</span>
            <span className="admin-trade-history__sr-touches">{l.touches as number} touches</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Multi-Timeframe Analysis ── */
function MtfDisplay({ mtf }: { mtf: Record<string, unknown> | null | undefined }) {
  if (!mtf) return null;
  const scores = mtf.timeframe_scores as Record<string, number> | undefined;
  const alignment = mtf.trend_alignment as boolean;
  return (
    <div className="admin-trade-history__mtf">
      <div className="admin-trade-history__mtf-header">
        <span>Trend Alignment: <strong>{alignment ? "ALIGNED" : "DIVERGENT"}</strong></span>
        <span>Consensus: <SignalBadge signal={mtf.consensus_signal as string} /></span>
      </div>
      {scores && (
        <div className="admin-trade-history__mtf-scores">
          {Object.entries(scores).map(([tf, sc]) => (
            <div key={tf} className="admin-trade-history__mtf-tf">
              <span className="admin-trade-history__mtf-tf-label">{tf}</span>
              <div className="admin-trade-history__ta-score-track">
                <div
                  className={`admin-trade-history__ta-score-fill admin-trade-history__ta-score-fill--${(sc as number) >= 65 ? "high" : (sc as number) >= 40 ? "mid" : "low"}`}
                  style={{ width: `${(sc as number)}%` }}
                />
              </div>
              <span className="admin-trade-history__ta-score-value">{(sc as number).toFixed(1)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Structured Filter Evaluations ── */
function FilterEvalDisplay({ evaluations }: { evaluations: TradeHistoryItem["filter_evaluations"] }) {
  if (!evaluations || evaluations.length === 0) return null;
  const byCategory: Record<string, typeof evaluations> = {};
  for (const ev of evaluations) {
    const cat = ev.category || "other";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(ev);
  }
  return (
    <div className="admin-trade-history__filter-eval">
      {Object.entries(byCategory).map(([cat, items]) => {
        const passedCount = items.filter((i) => i.passed).length;
        return (
          <div key={cat} className="admin-trade-history__filter-category">
            <div className="admin-trade-history__filter-category-header">
              <span className="admin-trade-history__filter-category-name">{cat.toUpperCase()}</span>
              <span className="admin-trade-history__filter-category-count">
                {passedCount}/{items.length} passed
              </span>
            </div>
            <div className="admin-trade-history__filter-items">
              {items.map((fe, i) => (
                <div key={i} className={`admin-trade-history__filter-item ${fe.passed ? "admin-trade-history__filter-item--pass" : "admin-trade-history__filter-item--fail"}`}>
                  <div className="admin-trade-history__filter-item-header">
                    <span className={`admin-trade-history__filter-item-status ${fe.passed ? "admin-trade-history__filter-item-status--pass" : "admin-trade-history__filter-item-status--fail"}`}>
                      {fe.passed ? "PASS" : "FAIL"}
                    </span>
                    <span className="admin-trade-history__filter-item-name">{fe.name.replace(/_/g, " ").toUpperCase()}</span>
                  </div>
                  <div className="admin-trade-history__filter-item-details">
                    {fe.actual_value !== null && (
                      <span>Value: {typeof fe.actual_value === "number" ? fe.actual_value.toFixed(4) : String(fe.actual_value)}</span>
                    )}
                    {(fe.threshold_low !== null || fe.threshold_high !== null) && (
                      <span>
                        [ {fe.threshold_low !== null ? (typeof fe.threshold_low === "number" ? fe.threshold_low.toFixed(4) : String(fe.threshold_low)) : "-∞"}
                        , {fe.threshold_high !== null ? (typeof fe.threshold_high === "number" ? fe.threshold_high.toFixed(4) : String(fe.threshold_high)) : "∞"} ]
                      </span>
                    )}
                    {fe.detail && <span className="admin-trade-history__filter-item-detail">{fe.detail}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface ExpandedTradeDetailsProps {
  trade: TradeHistoryItem;
}

function ExpandedTradeDetails({ trade }: ExpandedTradeDetailsProps) {
  const ta = trade.technical_analysis;
  const mtf = (ta?.multi_timeframe_analysis as Record<string, unknown>) ?? (ta?.metadata as Record<string, unknown>)?.multi_timeframe_deep_analysis;
  const indicators = ta?.indicators as Record<string, unknown> | undefined;
  const fullIndicators = (ta?.full_analysis as Record<string, unknown>)?.indicator_results as Array<Record<string, unknown>> | undefined;
  const patterns = (ta?.full_analysis as Record<string, unknown>)?.candlestick_patterns as Array<Record<string, unknown>> | undefined;
  const breakoutPatterns = ta?.patterns as Array<Record<string, unknown>> | undefined;
  const srLevels = ta?.support_resistance as Array<Record<string, unknown>> | undefined;
  const tradingSignal = ta?.trading_signal as Record<string, unknown> | undefined;
  const signal = tradingSignal?.signal as string | undefined;
  const signalStrength = tradingSignal?.strength as number | undefined;

  return (
    <td colSpan={9}>
      <div className="admin-trade-history__expanded-content">
        {/* Column 1: Trade Details */}
        <div className="admin-trade-history__detail-section">
          <h4 className="admin-trade-history__detail-section-title">Trade Details</h4>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Entry</span>
            <span className="admin-trade-history__detail-value">{formatPrice(trade.entry_price)}</span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Exit</span>
            <span className="admin-trade-history__detail-value">{formatPrice(trade.exit_price)}</span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Qty</span>
            <span className="admin-trade-history__detail-value">{trade.quantity ?? "-"}</span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">PnL USDT</span>
            <span className={`admin-trade-history__detail-value ${pnlClass(trade.pnl_usdt)}`}>
              {trade.pnl_usdt !== null ? `$${trade.pnl_usdt.toFixed(2)}` : "-"}
            </span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Stop Loss</span>
            <span className="admin-trade-history__detail-value">
              {formatPrice(trade.stop_loss_price)}
              {trade.sl_hit ? <span className="admin-trade-history__sl-hit"> HIT</span> : ""}
            </span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">TPs</span>
            <span className="admin-trade-history__detail-value">
              {trade.take_profit_prices
                ? trade.take_profit_prices.map((tp, i) => {
                    const hit =
                      (i === 0 && trade.tp1_hit) ||
                      (i === 1 && trade.tp2_hit) ||
                      (i === 2 && trade.tp3_hit);
                    return (
                      <span key={i} className={hit ? "admin-trade-history__tp-hit" : "admin-trade-history__tp-miss"}>
                        {formatPrice(tp)}
                        {hit ? " HIT" : ""}
                        {i < trade.take_profit_prices!.length - 1 ? ", " : ""}
                      </span>
                    );
                  })
                : "-"}
            </span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Open</span>
            <span className="admin-trade-history__detail-value">{formatDate(trade.trade_open_time)}</span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Close</span>
            <span className="admin-trade-history__detail-value">{formatDate(trade.trade_close_time)}</span>
          </div>
        </div>

        {/* Column 2: Scoring & Source */}
        <div className="admin-trade-history__detail-section">
          <h4 className="admin-trade-history__detail-section-title">Scoring & Source</h4>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Confidence</span>
            <span className="admin-trade-history__detail-value">{trade.confidence?.toFixed(2) ?? "-"}</span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">XGBoost M28</span>
            <span className="admin-trade-history__detail-value">
              {trade.xgboost_score != null ? (
                <span className={`admin-trade-history__outcome-badge admin-trade-history__outcome-badge--${trade.xgboost_passed ? "win" : "loss"}`}>
                  {(trade.xgboost_score * 100).toFixed(0)}% {trade.xgboost_passed ? "PASSED" : "REJECTED"}
                </span>
              ) : "-"}
            </span>
          </div>
          {trade.xgboost_model_version && (
            <div className="admin-trade-history__detail-row">
              <span className="admin-trade-history__detail-label">XGB Model</span>
              <span className="admin-trade-history__detail-value" style={{ fontFamily: "monospace" }}>
                {trade.xgboost_model_version}{trade.xgboost_threshold != null ? ` / thr ${trade.xgboost_threshold.toFixed(2)}` : ""}
              </span>
            </div>
          )}
          {trade.xgboost_top_features && trade.xgboost_top_features.length > 0 && (
            <div className="admin-trade-history__detail-section admin-trade-history__detail-section--full">
              <h4 className="admin-trade-history__detail-section-title">
                XGBoost Features ({trade.xgboost_top_features.length} features){trade.xgboost_reason ? ` — ${trade.xgboost_reason}` : ""}
              </h4>
              <div className="admin-trade-history__features-grid">
                {trade.xgboost_top_features.map((f, i) => (
                  <div key={i} className="admin-trade-history__feature-chip">
                    <span className="admin-trade-history__feature-chip-name">{f.name}</span>
                    <span className={`admin-trade-history__feature-chip-value ${f.value > 0 ? "admin-trade-history__feature-chip-value--pos" : "admin-trade-history__feature-chip-value--neg"}`}>
                      {f.value > 0 ? "+" : ""}{f.value.toFixed(4)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Final Score</span>
            <span className="admin-trade-history__detail-value">
              <ScoreBar score={trade.final_score} />
            </span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">MACRO Bias</span>
            <span className="admin-trade-history__detail-value">
              <span className={`admin-trade-history__macro-badge admin-trade-history__macro--${trade.macro_bias ?? "NEUTRAL"}`}>
                {trade.macro_bias ?? "N/A"}
              </span>
            </span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Source</span>
            <span className="admin-trade-history__detail-value">{trade.source ?? "-"}</span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">R:R Ratio</span>
            <span className="admin-trade-history__detail-value">
              {trade.risk_reward_ratio !== null ? trade.risk_reward_ratio.toFixed(2) : "-"}
            </span>
          </div>
          <div className="admin-trade-history__detail-row">
            <span className="admin-trade-history__detail-label">Rec. ID</span>
            <span className="admin-trade-history__detail-value" style={{ fontSize: "0.7rem", color: "#64748b" }}>
              {trade.recommendation_id ? trade.recommendation_id.slice(0, 12) + "..." : "-"}
            </span>
          </div>
        </div>

        {/* Section 3: Trading Signal & Scores */}
        {ta && (
          <div className="admin-trade-history__detail-section admin-trade-history__detail-section--full">
            <h4 className="admin-trade-history__detail-section-title">
              Technical Analysis
              {signal && <SignalBadge signal={signal} />}
              {signalStrength !== undefined && <span className="admin-trade-history__signal-strength">Strength: {signalStrength.toFixed(1)}%</span>}
            </h4>
            <div className="admin-trade-history__ta-scores-grid">
              <TaScoreBar label="Trend" score={ta.trend_score as number} />
              <TaScoreBar label="Momentum" score={ta.momentum_score as number} />
              <TaScoreBar label="Volatility" score={ta.volatility_score as number} />
              <TaScoreBar label="Volume" score={ta.volume_score as number} />
              <TaScoreBar label="Overall" score={ta.overall_score as number} />
            </div>
          </div>
        )}

        {/* Section 4: Multi-Timeframe Analysis */}
        {mtf && (
          <div className="admin-trade-history__detail-section admin-trade-history__detail-section--full">
            <h4 className="admin-trade-history__detail-section-title">Multi-Timeframe Analysis</h4>
            <MtfDisplay mtf={mtf as Record<string, unknown>} />
          </div>
        )}

        {/* Section 5: Indicators */}
        {indicators && (
          <div className="admin-trade-history__detail-section admin-trade-history__detail-section--full">
            <h4 className="admin-trade-history__detail-section-title">Key Indicators</h4>
            <IndicatorTable indicators={indicators} />
          </div>
        )}

        {/* Section 6: Full Indicator Results */}
        {fullIndicators && fullIndicators.length > 0 && (
          <div className="admin-trade-history__detail-section admin-trade-history__detail-section--full">
            <h4 className="admin-trade-history__detail-section-title">Full Indicator Analysis</h4>
            <FullIndicatorResults results={fullIndicators} />
          </div>
        )}

        {/* Section 7: Candlestick Patterns */}
        {patterns && patterns.length > 0 && (
          <div className="admin-trade-history__detail-section">
            <h4 className="admin-trade-history__detail-section-title">Candlestick Patterns</h4>
            <PatternList patterns={patterns} />
          </div>
        )}
        {breakoutPatterns && breakoutPatterns.length > 0 && !patterns && (
          <div className="admin-trade-history__detail-section">
            <h4 className="admin-trade-history__detail-section-title">Patterns</h4>
            <PatternList patterns={breakoutPatterns} />
          </div>
        )}

        {/* Section 8: Support / Resistance */}
        {srLevels && srLevels.length > 0 && (
          <div className="admin-trade-history__detail-section">
            <h4 className="admin-trade-history__detail-section-title">Support & Resistance</h4>
            <SrLevels levels={srLevels} />
          </div>
        )}

        {/* Section 9: Filter Evaluations */}
        <div className="admin-trade-history__detail-section admin-trade-history__detail-section--full">
          <h4 className="admin-trade-history__detail-section-title">Filter Evaluation Records</h4>
          {trade.filter_evaluations && trade.filter_evaluations.length > 0 ? (
            <FilterEvalDisplay evaluations={trade.filter_evaluations} />
          ) : (
            <div className="admin-trade-history__empty-text">No individual filter records available</div>
          )}
        </div>

        {/* Section 10: Analysis Text */}
        {trade.analysis && (
          <div className="admin-trade-history__detail-section">
            <h4 className="admin-trade-history__detail-section-title">Analysis</h4>
            <div className="admin-trade-history__detail-analysis">{trade.analysis}</div>
          </div>
        )}

        {/* Section 11: Success Factors */}
        {trade.success_factors && trade.success_factors.length > 0 && (
          <div className="admin-trade-history__detail-section">
            <h4 className="admin-trade-history__detail-section-title">Success Factors</h4>
            <ul className="admin-trade-history__detail-list admin-trade-history__detail-list--success">
              {trade.success_factors.map((f: string, i: number) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Section 12: Failure Reasons */}
        {trade.failure_reasons && trade.failure_reasons.length > 0 && (
          <div className="admin-trade-history__detail-section">
            <h4 className="admin-trade-history__detail-section-title">Failure Reasons</h4>
            <ul className="admin-trade-history__detail-list admin-trade-history__detail-list--failure">
              {trade.failure_reasons.map((r: string, i: number) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </td>
  );
}

const SORTABLE_COLUMNS = [
  { key: "created_at", label: "Date" },
  { key: "symbol", label: "Symbol" },
  { key: "outcome", label: "Result" },
  { key: "pnl_pct", label: "PnL" },
  { key: "confidence", label: "Conf." },
];

export function AdminTradeHistoryTab(): JSX.Element {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filter state
  const [timeRange, setTimeRange] = useState("90d");
  const [outcome, setOutcome] = useState("");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [hasTa, setHasTa] = useState("");
  const [macroBias, setMacroBias] = useState("");
  const [source, setSource] = useState("");
  const [slHit, setSlHit] = useState("");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const params: TradeHistoryParams = {
    time_range: timeRange,
    outcome: outcome || undefined,
    symbol: symbolFilter || undefined,
    has_ta: hasTa || undefined,
    macro_bias: macroBias || undefined,
    source: source || undefined,
    sl_hit: slHit || undefined,
    search: debouncedSearch || undefined,
    page,
    page_size: 50,
    sort_by: sortBy,
    sort_order: sortOrder,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "trade-history", params],
    queryFn: () => getTradeHistory(params),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });

  const filters: TradeHistoryFilters = data?.filters ?? {
    outcomes: ["win", "loss", "breakeven", "unknown"],
    symbols: [],
    macro_biases: [],
    directions: [],
    sources: [],
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 1;

  const stats = useMemo(() => {
    let wins = 0;
    let losses = 0;
    let breakevens = 0;
    let unknowns = 0;
    for (const item of items) {
      if (item.outcome === "win") wins++;
      else if (item.outcome === "loss") losses++;
      else if (item.outcome === "breakeven") breakevens++;
      else unknowns++;
    }
    return { wins, losses, breakevens, unknowns };
  }, [items]);

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPage(1);
  }

  function handleSearchChange(value: string) {
    setSearchText(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  }

  function clearFilters() {
    setTimeRange("90d");
    setOutcome("");
    setSymbolFilter("");
    setHasTa("");
    setMacroBias("");
    setSource("");
    setSlHit("");
    setSearchText("");
    setDebouncedSearch("");
    setPage(1);
    setSortBy("created_at");
    setSortOrder("desc");
  }

  function renderPagination() {
    if (total <= 1) return null;
    const pages: number[] = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return (
      <div className="admin-trade-history__pagination">
        <button
          className="admin-trade-history__page-btn"
          disabled={page <= 1}
          onClick={() => setPage(1)}
        >
          First
        </button>
        <button
          className="admin-trade-history__page-btn"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        >
          Prev
        </button>
        {start > 1 && <span className="admin-trade-history__page-info">...</span>}
        {pages.map((p) => (
          <button
            key={p}
            className={`admin-trade-history__page-btn ${p === page ? "admin-trade-history__page-btn--active" : ""}`}
            onClick={() => setPage(p)}
          >
            {p}
          </button>
        ))}
        {end < totalPages && <span className="admin-trade-history__page-info">...</span>}
        <button
          className="admin-trade-history__page-btn"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>
        <button
          className="admin-trade-history__page-btn"
          disabled={page >= totalPages}
          onClick={() => setPage(totalPages)}
        >
          Last
        </button>
        <span className="admin-trade-history__page-info">
          Page {page} / {totalPages} ({total} trades)
        </span>
      </div>
    );
  }

  const sortIndicator = (col: string) => {
    if (sortBy !== col) return "";
    return sortOrder === "asc" ? " ^" : " v";
  };

  /* ── Selection helpers ── */
  const currentPageIds = useMemo(() => new Set(items.map((t) => t.id)), [items]);
  const selectedOnPage = useMemo(
    () => items.filter((t) => selectedIds.has(t.id)),
    [items, selectedIds],
  );
  const allOnPageSelected = items.length > 0 && items.every((t) => selectedIds.has(t.id));

  function toggleSelectAllOnPage() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const id of currentPageIds) next.delete(id);
      } else {
        for (const id of currentPageIds) next.add(id);
      }
      return next;
    });
  }

  function toggleSelectOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  /* ── Export helpers ── */
  function flattenTradeForCsv(trade: TradeHistoryItem): Record<string, string> {
    const row: Record<string, string> = {
      Symbol: trade.symbol,
      Date: trade.created_at ? formatDate(trade.created_at) : "-",
      Outcome: trade.outcome,
      PnL_Pct: trade.pnl_pct !== null ? trade.pnl_pct.toFixed(2) : "-",
      PnL_USDT: trade.pnl_usdt !== null ? trade.pnl_usdt.toFixed(2) : "-",
      Entry_Price: trade.entry_price !== null ? trade.entry_price.toString() : "-",
      Exit_Price: trade.exit_price !== null ? trade.exit_price.toString() : "-",
      Quantity: trade.quantity !== null ? trade.quantity.toString() : "-",
      SL_Price: trade.stop_loss_price !== null ? trade.stop_loss_price.toString() : "-",
      SL_Hit: trade.sl_hit ? "Yes" : "No",
      TP1_Hit: trade.tp1_hit ? "Yes" : "No",
      TP2_Hit: trade.tp2_hit ? "Yes" : "No",
      TP3_Hit: trade.tp3_hit ? "Yes" : "No",
      Confidence: trade.confidence !== null ? trade.confidence.toFixed(2) : "-",
      Final_Score: trade.final_score !== null ? trade.final_score.toFixed(2) : "-",
      XGBoost_Score: trade.xgboost_score !== null ? trade.xgboost_score.toFixed(4) : "-",
      XGBoost_Passed: trade.xgboost_passed !== null ? (trade.xgboost_passed ? "Yes" : "No") : "-",
      XGBoost_Model: trade.xgboost_model_version ?? "-",
      XGBoost_Threshold: trade.xgboost_threshold !== null ? trade.xgboost_threshold.toFixed(2) : "-",
      MACRO_Bias: trade.macro_bias ?? "-",
      Source: trade.source ?? "-",
      R_R_Ratio: trade.risk_reward_ratio !== null ? trade.risk_reward_ratio.toFixed(2) : "-",
      Direction: trade.direction ?? "-",
    };

    // TA scores
    const ta = trade.technical_analysis as Record<string, unknown> | null;
    if (ta) {
      const scores = ["trend_score", "momentum_score", "volatility_score", "volume_score", "overall_score"];
      for (const s of scores) {
        const val = ta[s] as number | undefined;
        row[`TA_${s}`] = val !== undefined ? val.toFixed(1) : "-";
      }
      const ts = ta.trading_signal as Record<string, unknown> | undefined;
      if (ts) {
        row.TA_Signal = (ts.signal as string) ?? "-";
        row.TA_Signal_Strength = ts.strength !== undefined ? String(ts.strength) : "-";
      }
    }

    // Filter evaluations
    if (trade.filter_evaluations && trade.filter_evaluations.length > 0) {
      for (const fe of trade.filter_evaluations) {
        const prefix = `Filter_${fe.category}_${fe.name}`.replace(/\s+/g, "_");
        row[`${prefix}_Passed`] = fe.passed ? "Yes" : "No";
        row[`${prefix}_Value`] = fe.actual_value !== null ? String(fe.actual_value) : "-";
        row[`${prefix}_ThrLow`] = fe.threshold_low !== null ? String(fe.threshold_low) : "-";
        row[`${prefix}_ThrHigh`] = fe.threshold_high !== null ? String(fe.threshold_high) : "-";
      }
    }

    // XGBoost top features
    if (trade.xgboost_top_features && trade.xgboost_top_features.length > 0) {
      for (const f of trade.xgboost_top_features) {
        row[`XGB_Feature_${f.name}`] = f.value.toFixed(6);
      }
    }

    // Success/Failure
    row.Success_Factors = trade.success_factors?.join("; ") ?? "-";
    row.Failure_Reasons = trade.failure_reasons?.join("; ") ?? "-";
    row.Analysis = trade.analysis ?? "-";

    return row;
  }

  function downloadCsv() {
    const trades = items.filter((t) => selectedIds.has(t.id));
    if (trades.length === 0) return;

    const rows = trades.map(flattenTradeForCsv);
    // Collect all unique headers
    const headers = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
    const escapeVal = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csvLines = [
      headers.map(escapeVal).join(","),
      ...rows.map((r) => headers.map((h) => escapeVal(r[h] ?? "-")).join(",")),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trade_history_${trades.length}_trades_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadJson() {
    const trades = items.filter((t) => selectedIds.has(t.id));
    if (trades.length === 0) return;
    const blob = new Blob([JSON.stringify(trades, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trade_history_${trades.length}_trades_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadPdf() {
    const trades = items.filter((t) => selectedIds.has(t.id));
    if (trades.length === 0) return;

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 15;

    // Title
    doc.setFontSize(16);
    doc.setTextColor(30, 41, 59);
    doc.text("Trade History Report", pageWidth / 2, y, { align: "center" });
    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${new Date().toLocaleString()}  |  ${trades.length} trade(s) selected`, pageWidth / 2, y, { align: "center" });
    y += 10;

    for (let ti = 0; ti < trades.length; ti++) {
      const t = trades[ti];
      const pnlColor = (t.pnl_pct ?? 0) >= 0 ? [34, 197, 94] : [239, 68, 68];
      const outcomeClr = t.outcome === "win" ? [34, 197, 94] : t.outcome === "loss" ? [239, 68, 68] : [251, 191, 36];

      // Check if we need a new page for this trade
      if (y > 170) {
        doc.addPage();
        y = 15;
      }

      // Separator line before each trade (except first)
      if (ti > 0) {
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.5);
        doc.line(14, y, pageWidth - 14, y);
        y += 4;
      }

      // Trade header
      doc.setFillColor(15, 23, 42);
      doc.rect(14, y, pageWidth - 28, 10, "F");
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(`#${ti + 1}  ${t.symbol}`, 18, y + 7);

      // Outcome badge
      const outcomeW = 20;
      doc.setFillColor(outcomeClr[0], outcomeClr[1], outcomeClr[2]);
      doc.rect(pageWidth - 60, y + 2, outcomeW, 6, "F");
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(t.outcome.toUpperCase(), pageWidth - 50, y + 6.5, { align: "center" });

      // PnL badge
      const pnlText = t.pnl_pct !== null ? `${t.pnl_pct >= 0 ? "+" : ""}${t.pnl_pct.toFixed(2)}%` : "-";
      doc.setFontSize(13);
      doc.setTextColor(pnlColor[0], pnlColor[1], pnlColor[2]);
      doc.text(pnlText, pageWidth - 25, y + 7, { align: "right" });
      y += 13;

      // ---- Row 1: Basic info ----
      const infoTable = [
        ["Entry", formatPrice(t.entry_price), "Exit", formatPrice(t.exit_price),
         "Qty", t.quantity ?? "-", "P&L USDT", t.pnl_usdt !== null ? `$${t.pnl_usdt.toFixed(2)}` : "-"],
        ["Open", formatDate(t.trade_open_time), "Close", formatDate(t.trade_close_time),
         "MACRO", t.macro_bias ?? "-", "Source", t.source ?? "-"],
      ];

      autoTable(doc, {
        startY: y,
        body: infoTable,
        theme: "plain",
        styles: { fontSize: 8, cellPadding: 2, textColor: [30, 41, 59] },
        columnStyles: {
          0: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 18 },
          1: { cellWidth: 32 },
          2: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 18 },
          3: { cellWidth: 32 },
          4: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 18 },
          5: { cellWidth: 32 },
          6: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 18 },
          7: { cellWidth: 32 },
        },
        margin: { left: 14 },
        tableWidth: pageWidth - 28,
      });
      y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 3;

      // ---- Row 2: Scoring ----
      const scoringTable = [
        ["Confidence", t.confidence?.toFixed(2) ?? "-",
         "Final Score", t.final_score?.toFixed(2) ?? "-",
         "R:R", t.risk_reward_ratio?.toFixed(2) ?? "-"],
        ["XGBoost", t.xgboost_score != null ? `${(t.xgboost_score * 100).toFixed(0)}% ${t.xgboost_passed ? "PASSED" : "REJECTED"}` : "-",
         "XGB Model", t.xgboost_model_version ?? "-",
         "Thr", t.xgboost_threshold?.toFixed(2) ?? "-"],
      ];
      autoTable(doc, {
        startY: y,
        body: scoringTable,
        theme: "plain",
        styles: { fontSize: 8, cellPadding: 2, textColor: [30, 41, 59] },
        columnStyles: {
          0: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 22 },
          1: { cellWidth: 50 },
          2: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 22 },
          3: { cellWidth: 50 },
          4: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 12 },
          5: { cellWidth: 18 },
        },
        margin: { left: 14 },
        tableWidth: pageWidth - 28,
      });
      y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 3;

      // ---- Row 3: TA Scores ----
      const ta = t.technical_analysis as Record<string, unknown> | null;
      if (ta) {
        const taRows = [
          ["Trend", ta.trend_score != null ? String(ta.trend_score) : "-",
           "Momentum", ta.momentum_score != null ? String(ta.momentum_score) : "-",
           "Volatility", ta.volatility_score != null ? String(ta.volatility_score) : "-",
           "Volume", ta.volume_score != null ? String(ta.volume_score) : "-",
           "Overall", ta.overall_score != null ? String(ta.overall_score) : "-"],
        ];
        const ts = ta.trading_signal as Record<string, unknown> | undefined;
        if (ts?.signal) {
          taRows[0].push("Signal", String(ts.signal));
        }

        autoTable(doc, {
          startY: y,
          body: taRows,
          theme: "plain",
          styles: { fontSize: 8, cellPadding: 2, textColor: [30, 41, 59] },
          columnStyles: {
            0: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 22 },
            1: { cellWidth: 26 },
            2: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 22 },
            3: { cellWidth: 26 },
            4: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 22 },
            5: { cellWidth: 26 },
            6: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 22 },
            7: { cellWidth: 26 },
          },
          margin: { left: 14 },
          tableWidth: pageWidth - 28,
        });
        y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 3;
      }

      // ---- Row 4: Filter Evaluations Summary ----
      const feList = t.filter_evaluations || [];
      if (feList.length > 0) {
        const byCat: Record<string, typeof feList> = {};
        for (const fe of feList) {
          const cat = fe.category || "other";
          if (!byCat[cat]) byCat[cat] = [];
          byCat[cat].push(fe);
        }

        // Build filter summary text per category
        const filterRows: string[][] = [];
        for (const [cat, items] of Object.entries(byCat)) {
          const passed = items.filter((f) => f.passed).length;
          const names = items.map((f) => `${f.name.replace(/_/g, " ")} (${f.passed ? "PASS" : "FAIL"})`).join(", ");
          filterRows.push([cat.toUpperCase(), `${passed}/${items.length} passed`, names]);
        }

        autoTable(doc, {
          startY: y,
          head: [["Filter Category", "Status", "Details"]],
          body: filterRows,
          theme: "striped",
          headStyles: { fillColor: [51, 65, 85], textColor: [255, 255, 255], fontSize: 8 },
          styles: { fontSize: 7, cellPadding: 2, textColor: [30, 41, 59] },
          columnStyles: {
            0: { cellWidth: 38, fontStyle: "bold" },
            1: { cellWidth: 22 },
            2: { cellWidth: pageWidth - 102, fontStyle: "italic" },
          },
          margin: { left: 14 },
          tableWidth: pageWidth - 28,
        });
        y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 3;
      }

      // ---- Row 5: Success / Failure factors ----
      if (t.success_factors?.length || t.failure_reasons?.length) {
        const factorRows: string[][] = [];
        if (t.success_factors?.length) {
          factorRows.push(["Success Factors", t.success_factors.join(" | ")]);
        }
        if (t.failure_reasons?.length) {
          factorRows.push(["Failure Reasons", t.failure_reasons.join(" | ")]);
        }
        if (t.analysis) {
          factorRows.push(["Analysis", t.analysis]);
        }
        autoTable(doc, {
          startY: y,
          body: factorRows,
          theme: "plain",
          styles: { fontSize: 7, cellPadding: 2, textColor: [30, 41, 59] },
          columnStyles: {
            0: { fontStyle: "bold", fillColor: [241, 245, 249], cellWidth: 30 },
            1: { cellWidth: pageWidth - 72, fontStyle: "italic" },
          },
          margin: { left: 14 },
          tableWidth: pageWidth - 28,
        });
        y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
      }

      y += 4;
      // ---- XGBoost Top Features ----
      const xgbFeats = t.xgboost_top_features || [];
      if (xgbFeats.length > 0) {
        const topFeats = xgbFeats.slice(0, 10).map((f) => `${f.name}=${f.value >= 0 ? "+" : ""}${f.value.toFixed(4)}`).join("  |  ");
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        doc.text(`XGBoost Top Features:  ${topFeats}`, 14, y, { maxWidth: pageWidth - 28 });
        y += 5;
      }

      // Thick separator after each trade
      doc.setDrawColor(99, 102, 241);
      doc.setLineWidth(0.8);
      doc.line(14, y, pageWidth - 14, y);
      y += 6;
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Trade History Report — ${trades.length} trade(s) — zakyam.com`, pageWidth / 2, 205, { align: "center" });

    doc.save(`trade_history_${trades.length}_trades_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  if (error) {
    return (
      <div className="admin-trade-history">
        <div className="admin-trade-history__header">
          <h2>Trade History</h2>
        </div>
        <div className="admin-trade-history__error">
          Failed to load trade history. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="admin-trade-history">
      <div className="admin-trade-history__header">
        <h2>Trade History</h2>
        <div className="admin-trade-history__stats">
          <span className="admin-trade-history__stat">
            Wins: <span className="admin-trade-history__stat-value admin-trade-history__stat-value--win">{stats.wins}</span>
          </span>
          <span className="admin-trade-history__stat">
            Losses: <span className="admin-trade-history__stat-value admin-trade-history__stat-value--loss">{stats.losses}</span>
          </span>
          <span className="admin-trade-history__stat">
            Breakeven: <span className="admin-trade-history__stat-value admin-trade-history__stat-value--breakeven">{stats.breakevens}</span>
          </span>
          <span className="admin-trade-history__stat">
            Unknown: <span className="admin-trade-history__stat-value admin-trade-history__stat-value--unknown">{stats.unknowns}</span>
          </span>
          <span className="admin-trade-history__stat">
            Total: <span className="admin-trade-history__stat-value">{items.length}</span>
          </span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="admin-trade-history__filters">
        <div className="admin-trade-history__filter-group">
          <span className="admin-trade-history__filter-label">Period</span>
          <select
            className="admin-trade-history__filter-select"
            value={timeRange}
            onChange={(e) => { setTimeRange(e.target.value); setPage(1); }}
          >
            {TIME_RANGES.map((tr) => (
              <option key={tr.value} value={tr.value}>{tr.label}</option>
            ))}
          </select>
        </div>

        <div className="admin-trade-history__filter-group">
          <span className="admin-trade-history__filter-label">Outcome</span>
          <select
            className="admin-trade-history__filter-select"
            value={outcome}
            onChange={(e) => { setOutcome(e.target.value); setPage(1); }}
          >
            <option value="">All</option>
            {filters.outcomes.map((o) => (
              <option key={o} value={o}>{outcomeLabel(o)}</option>
            ))}
          </select>
        </div>

        <div className="admin-trade-history__filter-group">
          <span className="admin-trade-history__filter-label">Symbol</span>
          <select
            className="admin-trade-history__filter-select"
            value={symbolFilter}
            onChange={(e) => { setSymbolFilter(e.target.value); setPage(1); }}
          >
            <option value="">All</option>
            {filters.symbols.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="admin-trade-history__filter-group">
          <span className="admin-trade-history__filter-label">TA</span>
          <select
            className="admin-trade-history__filter-select"
            value={hasTa}
            onChange={(e) => { setHasTa(e.target.value); setPage(1); }}
          >
            <option value="">All</option>
            <option value="yes">Has TA</option>
            <option value="no">No TA</option>
          </select>
        </div>

        <div className="admin-trade-history__filter-group">
          <span className="admin-trade-history__filter-label">MACRO</span>
          <select
            className="admin-trade-history__filter-select"
            value={macroBias}
            onChange={(e) => { setMacroBias(e.target.value); setPage(1); }}
          >
            <option value="">All</option>
            {filters.macro_biases.map((mb) => (
              <option key={mb} value={mb}>{mb}</option>
            ))}
          </select>
        </div>

        <div className="admin-trade-history__filter-group">
          <span className="admin-trade-history__filter-label">Source</span>
          <select
            className="admin-trade-history__filter-select"
            value={source}
            onChange={(e) => { setSource(e.target.value); setPage(1); }}
          >
            <option value="">All</option>
            {filters.sources.map((src) => (
              <option key={src} value={src}>{src}</option>
            ))}
          </select>
        </div>

        <div className="admin-trade-history__filter-group">
          <span className="admin-trade-history__filter-label">SL</span>
          <select
            className="admin-trade-history__filter-select"
            value={slHit}
            onChange={(e) => { setSlHit(e.target.value); setPage(1); }}
          >
            <option value="">All</option>
            <option value="yes">SL Hit</option>
            <option value="no">No SL</option>
          </select>
        </div>

        <input
          className="admin-trade-history__filter-search"
          type="text"
          placeholder="Search symbol/analysis..."
          value={searchText}
          onChange={(e) => handleSearchChange(e.target.value)}
        />

        <button className="admin-trade-history__filter-clear-btn" onClick={clearFilters}>
          Clear
        </button>
      </div>

      {/* Export toolbar — visible when items are selected */}
      {selectedOnPage.length > 0 && (
        <div className="admin-trade-history__export-bar">
          <span className="admin-trade-history__export-count">
            {selectedOnPage.length} trade{selectedOnPage.length > 1 ? "s" : ""} selected
          </span>
          <button className="admin-trade-history__export-btn" onClick={downloadCsv}>
            Download CSV
          </button>
          <button className="admin-trade-history__export-btn" onClick={downloadPdf}>
            Download PDF
          </button>
          <button className="admin-trade-history__export-btn" onClick={downloadJson}>
            Download JSON
          </button>
          <button className="admin-trade-history__export-btn admin-trade-history__export-btn--clear" onClick={clearSelection}>
            Clear Selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="admin-trade-history__table-wrapper">
        {isLoading ? (
          <div className="admin-trade-history__loading">Loading trade history...</div>
        ) : items.length === 0 ? (
          <div className="admin-trade-history__empty">
            <span>No trades found matching filters.</span>
          </div>
        ) : (
          <table className="admin-trade-history__table">
            <thead>
              <tr>
                <th className="admin-trade-history__checkbox-col">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    title="Select all on this page"
                  />
                </th>
                {SORTABLE_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className={sortBy === col.key ? "sorted" : ""}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label} {sortIndicator(col.key)}
                  </th>
                ))}
                <th>XGB</th>
                <th>MACRO</th>
                <th>Score</th>
                <th>TA</th>
              </tr>
            </thead>
            <tbody>
              {items.map((trade) => (
                <Fragment key={trade.id}>
                  <tr
                    onClick={() => {
                      setExpandedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(trade.id)) { next.delete(trade.id); } else { next.add(trade.id); }
                        return next;
                      });
                    }}
                    className={expandedIds.has(trade.id) ? "admin-trade-history__expanded-row" : ""}
                  >
                    <td className="admin-trade-history__checkbox-col" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(trade.id)}
                        onChange={() => toggleSelectOne(trade.id)}
                      />
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDate(trade.created_at)}</td>
                    <td>
                      <strong>{trade.symbol}</strong>
                    </td>
                    <td>
                      <span
                        className={`admin-trade-history__outcome-badge admin-trade-history__outcome-badge--${trade.outcome}`}
                      >
                        <span style={{ color: outcomeColor(trade.outcome) }}>•</span>
                        {outcomeLabel(trade.outcome)}
                      </span>
                    </td>
                    <td className={pnlClass(trade.pnl_pct)}>
                      {formatPnl(trade.pnl_pct)}
                    </td>
                    <td>
                      <ScoreBar score={trade.confidence} />
                    </td>
                    <td>
                      {trade.xgboost_score != null ? (
                        <span
                          className={`admin-trade-history__outcome-badge admin-trade-history__outcome-badge--${trade.xgboost_passed ? "win" : "loss"}`}
                          title={`XGBoost M28: ${(trade.xgboost_score * 100).toFixed(0)}%${trade.xgboost_passed ? " (PASSED)" : " (REJECTED)"}`}
                        >
                          {(trade.xgboost_score * 100).toFixed(0)}%
                        </span>
                      ) : (
                        <span style={{ color: "#666", fontSize: "0.8rem" }}>-</span>
                      )}
                    </td>
                    <td>
                      <span className={`admin-trade-history__macro-badge admin-trade-history__macro--${trade.macro_bias ?? "NEUTRAL"}`}>
                        {trade.macro_bias ?? "N/A"}
                      </span>
                    </td>
                    <td>
                      <ScoreBar score={trade.final_score} />
                    </td>
                    <td>
                      <span className={`admin-trade-history__ta-badge admin-trade-history__ta-badge--${trade.has_ta ? "yes" : "no"}`}>
                        {trade.has_ta ? "Yes" : "No"}
                      </span>
                    </td>
                  </tr>
                  {expandedIds.has(trade.id) && (
                    <tr className="admin-trade-history__expanded-row">
                      <ExpandedTradeDetails trade={trade} />
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {renderPagination()}
    </div>
  );
}
